import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GetSurveyList, PublishSurvey, ArchiveSurvey, GetSurveyById } from "../../../utils/apiCalls/surveyBuilder";
import { downloadJSON } from "../../../utils/downloadFile";
import { useToast } from "../../../utils/ToastContext";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import useConfirmDialog from "../../../hooks/useConfirmDialog";
import { SURVEY_STATUS } from "../../../utils/surveysConstants";

const SEARCH_KEY = "surveysListSearch";
const PAGE_KEY = "surveysListPage";

export default function useSurveysList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(() => Number(sessionStorage.getItem(PAGE_KEY)) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem(SEARCH_KEY) || "");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  // Landing here after a builder redirect (e.g. "a draft already exists")
  // arrives with a status to pre-filter to — see useSurveyBuilder's handleSave.
  const [statusFilter, setStatusFilter] = useState(() => location.state?.statusFilter || "");
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [assignSurvey, setAssignSurvey] = useState(null);
  const archiveDialog = useConfirmDialog();
  // Only one Draft can exist per surveyGroupId at a time (backend rule).
  // Tracked independently of the current page/filter/search so "Edit" on a
  // Published/Archived row can jump straight to that existing Draft instead
  // of firing a clone request the backend will reject.
  const [draftByGroupId, setDraftByGroupId] = useState(new Map());

  const fetchDraftGroups = useCallback(async () => {
    // pageSize:200 is a ceiling on how many Draft surveys we scan for a
    // match; an account with more drafts than that could miss one, but
    // useSurveyBuilder's save-time message check backstops that case.
    const response = await GetSurveyList({ pageIndex: 0, pageSize: 200, status: SURVEY_STATUS.DRAFT });
    if (response.statusCode === 200) {
      setDraftByGroupId(new Map(response.data.map((row) => [row.surveyGroupId, row])));
    }
  }, []);

  useEffect(() => {
    fetchDraftGroups();
  }, [fetchDraftGroups]);

  const fetchSurveys = useCallback(async (pageIndex, pageSize, searchString, status) => {
    setIsLoading(true);
    setFetchError("");
    try {
      const response = await GetSurveyList({ pageIndex, pageSize, searchString, status });
      if (response.statusCode === 200) {
        setRows(response.data);
        setTotalRows(response.totalRecords);
      } else {
        setRows([]);
        setFetchError(response.message || "Failed to load surveys");
      }
    } catch (error) {
      setRows([]);
      setFetchError(error?.message || "Failed to load surveys");
    }
    setIsLoading(false);
  }, []);

  const retryFetch = () => fetchSurveys(page, rowsPerPage, debouncedSearchQuery, statusFilter);

  useEffect(() => {
    fetchSurveys(page, rowsPerPage, debouncedSearchQuery, statusFilter);
  }, [fetchSurveys, debouncedSearchQuery, page, rowsPerPage, statusFilter]);

  useEffect(() => {
    sessionStorage.setItem(SEARCH_KEY, debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const handleSearchChange = (event) => setSearchQuery(event.target.value);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem(PAGE_KEY, String(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreate = () => {
    sessionStorage.removeItem("surveyRowData");
    navigate("/survey-form");
  };

  const handleEdit = (row) => {
    let target = row;
    if (row.status !== SURVEY_STATUS.DRAFT && draftByGroupId.has(row.surveyGroupId)) {
      target = draftByGroupId.get(row.surveyGroupId);
      showToast("A draft of this survey already exists — opening it instead.", "info");
    }
    sessionStorage.setItem("surveyRowData", JSON.stringify(target));
    navigate("/survey-form", { state: { rowData: target } });
  };

  const handleViewResults = (row) => {
    navigate("/survey-results", { state: { surveyId: row.id, surveyTitle: row.title } });
  };

  const handlePublish = async (row) => {
    const res = await PublishSurvey(row.id);
    if (res.statusCode === 200) {
      showToast("Survey published", "success");
      retryFetch();
      fetchDraftGroups();
    } else {
      showToast(res.message || "Failed to publish survey", "error");
    }
  };

  const handleArchiveClick = (row) => archiveDialog.openDialog(row);

  const confirmArchive = async () => {
    const row = archiveDialog.target;
    const res = await ArchiveSurvey(row.id);
    if (res.statusCode === 200) {
      showToast("Survey archived", "success");
      retryFetch();
      fetchDraftGroups();
    } else {
      showToast(res.message || "Failed to archive survey", "error");
    }
    archiveDialog.closeDialog();
  };

  const [viewSurvey, setViewSurvey] = useState(null);
  const handleViewClick = (row) => setViewSurvey(row);
  const closeViewModal = () => setViewSurvey(null);

  const handleAssignClick = (row) => setAssignSurvey(row);
  const closeAssignModal = () => setAssignSurvey(null);
  const handleAssigned = () => retryFetch();

  const handleExportJSON = async (row) => {
    try {
      const res = await GetSurveyById(row.id);
      if (res?.statusCode === 200 && res?.data) {
        downloadJSON(`${res.data.title || row.title || "survey"}-definition.json`, res.data);
        showToast("Survey JSON exported", "success");
      } else {
        downloadJSON(`${row.title || "survey"}-definition.json`, row);
        showToast("Survey JSON exported", "success");
      }
    } catch (err) {
      downloadJSON(`${row.title || "survey"}-definition.json`, row);
      showToast("Survey JSON exported", "success");
    }
  };

  return {
    rows,
    page,
    rowsPerPage,
    searchQuery,
    statusFilter,
    totalRows,
    isLoading,
    fetchError,
    assignSurvey,
    viewSurvey,
    archiveDialog,
    handleSearchChange,
    handleStatusFilterChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleCreate,
    handleEdit,
    handleViewResults,
    handleViewClick,
    closeViewModal,
    handlePublish,
    handleArchiveClick,
    confirmArchive,
    handleAssignClick,
    closeAssignModal,
    handleAssigned,
    handleExportJSON,
    retryFetch,
  };
}
